import React, { useState, useEffect, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import { formatCurrency, formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { loanMappingService } from 'api/services/loanMappingService';
import { combinedService } from 'api/services/combinedService';
import { CustomerFormModal } from 'components/modals/modules/customer-module/CustomerRegistryModal';
import type { CustomerFormData } from 'components/modals/modules/customer-module/CustomerRegistryModal';
import { useCustomers, useCreateCustomer, useGoldRates, useSystemConfig, useEnumsByGroup } from 'hooks/api';
import { ReturnedHistorySection } from 'components/common/ReturnedHistorySection';
import { ModalLoadingSkeleton } from 'components/common';
import { approvalService } from 'api/services/approvalService';

// ============================================================================
// Types
// ============================================================================

export interface ClosingGLOption {
  mappedGlId: string;
  glNumber: string;
  customerName: string;
  netWeight: number;
  allocationRatio: number;
  selected: boolean;
  outstandingPrincipal: string;
  outstandingInterest: string;
}

export interface MappingOption {
  id: string;
  mappingRef: string;
  customerName: string;
  customerId: string;
  loanAccountNumber: string;
  approvedAmount: number;
  outstandingPrincipal: number;
  outstandingInterest: number;
  gls: { mappedGlId: string; glNumber: string; customerName: string; netWeight: number; allocationRatio: number; status: string }[];
}

export interface NewLotGLFormData {
  id: string;
  glCustomerId: string;
  glCustomerName: string;
  glNumber: string;
  netWeight: string;
  originalLoanAmount: string;
  /** CR-002: outstanding GL principal — required on the Gross (FULL) rate basis; defaults from original amount */
  outstandingAmount: string;
  loanDate: string;
}

export interface CombinedFormData {
  closingMappingId: string;
  closingMappingRef: string;
  closingCustomerName: string;
  closingCustomerId: string;
  closingLoanAccountNumber: string;
  approvedAmount: number;
  addonOutstandingPrincipal: string;
  addonOutstandingInterest: string;
  closingGLs: ClosingGLOption[];
  newLotCustomerId: string;
  newLotCustomerName: string;
  newLotLoanType: string;
  newLotAccountNumber: string;
  newLotRequestedAmount: string;
  newLotGLs: NewLotGLFormData[];
}

export interface CombinedRecord {
  id: string;
  combinedRef: string;
  closingRef: string;
  closingMappingRef: string;
  closingCustomerName: string;
  closingCustomerId: string;
  closingLoanAccountNumber: string;
  addonOutstandingPrincipal: number;
  addonOutstandingInterest: number;
  combinedRatio: number;
  addonSettlementPrincipal: number;
  addonSettlementInterest: number;
  closingGLs: {
    glNumber: string;
    netWeight: number;
    allocationRatio: number;
    outstandingPrincipal: number;
    outstandingInterest: number;
  }[];
  closingTotalSettlement: number;
  newRequestRef: string;
  newLotCustomerName: string;
  newLotLoanType: string;
  newLotAccountNumber: string;
  newLotRequestedAmount: number;
  newLotGLs: {
    glNumber: string;
    customerName: string;
    netWeight: number;
    originalLoanAmount: number;
    loanDate: string;
  }[];
  netCashFlow: number;
  status: string;
  submittedBy?: string;
  submittedByName?: string;
  submittedAt?: string;
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  businessUnitName?: string;
}

// ============================================================================
// Shared data & helpers
// ============================================================================

// Constants removed — customer data, gold rate, and rounding unit are now fetched from API hooks inside the modal

const getAmountIndicator = (amount: number, eligible: number, maxPermissible: number): { color: string; label: string } => {
  if (amount <= 0 || maxPermissible <= 0) return { color: 'var(--text-muted)', label: '' };
  if (amount <= eligible) return { color: '#16a34a', label: 'Within Eligible' };
  if (amount <= maxPermissible) return { color: '#d97706', label: 'Between Eligible & Max' };
  return { color: '#dc2626', label: 'Exceeds Maximum' };
};

// LOAN_TYPE_OPTIONS now fetched from API via useEnumsByGroup('LOAN_TYPE') inside the modal

// ============================================================================
// Form Modal
// ============================================================================

interface CombinedFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  formData: CombinedFormData;
  mappingOptions: MappingOption[];
  onFormDataChange: (data: CombinedFormData) => void;
  onSave: () => void;
  onSubmit?: () => void;
  onClose: () => void;
}

export const CombinedFormModal: React.FC<CombinedFormModalProps> = ({
  isOpen,
  mode,
  formData,
  mappingOptions: _mappingOptions,
  onFormDataChange,
  onSave,
  onSubmit,
  onClose,
}) => {
  // Search state
  const [searchAccountNo, setSearchAccountNo] = useState('');
  const [searchGLAccountNo, setSearchGLAccountNo] = useState('');
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [validated, setValidated] = useState(false);

  // Customer modal state
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [customerFormData, setCustomerFormData] = useState<CustomerFormData>({ customerId: '', customerName: '' });
  const [customerModalSource, setCustomerModalSource] = useState<'section' | string>('section');

  // Fetch real data from API
  const { data: customers, refetch: refetchCustomers } = useCustomers();
  const { createCustomer } = useCreateCustomer();
  const { data: goldRates } = useGoldRates();
  const { data: systemConfigs } = useSystemConfig();
  const { data: loanTypeEnums } = useEnumsByGroup('LOAN_TYPE');

  // Dropdown value = UUID (c.id) so backend receives the correct FK
  const customerOptions = useMemo(() =>
    customers.map(c => ({ value: String(c.id), label: `${c.customerId} - ${c.customerName}` })),
    [customers]
  );
  const customerNameLookup = useMemo(() => {
    const map: Record<string, string> = {};
    customers.forEach(c => { map[String(c.id)] = c.customerName; });
    return map;
  }, [customers]);
  const loanTypeOptions = useMemo(() =>
    loanTypeEnums.map(e => ({ value: e.enumKey, label: e.enumValue })),
    [loanTypeEnums]
  );
  const activeGoldRate = useMemo(() =>
    goldRates.find(r => r.isActive)?.ratePerGram ?? 0,
    [goldRates]
  );
  const isGrossBasis = useMemo(() =>
    (goldRates.find(r => r.isActive)?.rateBasis ?? 'NET') === 'FULL',
    [goldRates]
  );
  const activeGoldRateId = useMemo(() => {
    const active = goldRates.find(r => r.isActive);
    return active ? String(active.id) : '';
  }, [goldRates]);
  const roundingUnit = useMemo(() => {
    const cfg = systemConfigs.find(c => c.configKey === 'rounding_unit');
    return cfg ? parseInt(cfg.configValue, 10) || 5000 : 5000;
  }, [systemConfigs]);

  useEffect(() => {
    if (isOpen) {
      setSearchAccountNo('');
      setSearchGLAccountNo('');
      setSearchPerformed(mode === 'edit');
      setSearchError('');
      setValidated(false);
    }
  }, [isOpen, mode]);

  if (!isOpen) return null;

  const isSearchDone = mode === 'edit' || (searchPerformed && !!formData.closingMappingId);

  // ---- Closing leg calculations ----
  const selectedClosingGLs = formData.closingGLs.filter((gl) => gl.selected);
  const closingCombinedRatio = selectedClosingGLs.reduce((s, gl) => s + gl.allocationRatio, 0);
  const addonP = parseFloat(formData.addonOutstandingPrincipal) || 0;
  const addonI = parseFloat(formData.addonOutstandingInterest) || 0;
  const settlementP = Math.round(addonP * closingCombinedRatio);
  const settlementI = addonI;
  const totalClosingGLPI = selectedClosingGLs.reduce((s, gl) =>
    s + (parseFloat(gl.outstandingPrincipal) || 0) + (parseFloat(gl.outstandingInterest) || 0), 0);
  const closingTotalSettlement = totalClosingGLPI + settlementP + settlementI;

  // GL validation
  const closingGLsWithMissing = selectedClosingGLs.filter(
    (gl) => !gl.outstandingPrincipal || !gl.outstandingInterest
  );

  // New lot GL validation (CR-002: outstanding required on gross basis)
  const newLotGLsWithMissing = formData.newLotGLs.filter(
    (gl) => !gl.glCustomerId || !gl.glNumber.trim() || gl.glNumber.length !== 14 || !gl.netWeight || !gl.originalLoanAmount || !gl.loanDate
      || (isGrossBasis && !(parseFloat(gl.outstandingAmount) > 0))
  );

  // ---- New lot calculations (CR-002 basis-aware) ----
  const newLotTotalWeight = formData.newLotGLs.reduce((s, gl) => s + (parseFloat(gl.netWeight) || 0), 0);
  const newLotTotalGLAmount = formData.newLotGLs.reduce((s, gl) => s + (parseFloat(gl.originalLoanAmount) || 0), 0);
  const newLotTotalOutstanding = formData.newLotGLs.reduce((s, gl) => s + (parseFloat(gl.outstandingAmount) || 0), 0);
  const newLotGrossValue = Math.round(newLotTotalWeight * activeGoldRate);
  const newLotEligible = isGrossBasis ? newLotGrossValue - newLotTotalOutstanding : newLotGrossValue;
  const newLotMaxPermissible = newLotEligible > 0 ? Math.ceil(newLotEligible / roundingUnit) * roundingUnit : 0;
  const newLotRequested = parseFloat(formData.newLotRequestedAmount) || 0;
  const newLotIndicator = getAmountIndicator(newLotRequested, newLotEligible, newLotMaxPermissible);
  const exceedsMax = newLotRequested > 0 && newLotMaxPermissible > 0 && newLotRequested > newLotMaxPermissible;
  const eligibilityBlocked = isGrossBasis && newLotTotalWeight > 0 && newLotEligible <= 0;

  // Net cash flow
  const netCashFlow = (newLotRequested + newLotTotalGLAmount) - closingTotalSettlement;

  // 14-character length validation for new lot fields
  const newLotAccNumError =
    formData.newLotAccountNumber && formData.newLotAccountNumber.length !== 14
      ? 'Must be exactly 14 characters'
      : '';
  const newLotGLNumberErrors = formData.newLotGLs.map((gl) =>
    gl.glNumber && gl.glNumber.length !== 14 ? 'Must be exactly 14 characters' : ''
  );
  const hasAccountErrors = !!newLotAccNumError || newLotGLNumberErrors.some(Boolean);

  // ---- Search ----
  const handleSearch = async () => {
    const accNo = searchAccountNo.trim().replace(/\s/g, '');
    const glAccNo = searchGLAccountNo.trim().replace(/\s/g, '');
    if (!accNo && !glAccNo) {
      setSearchError('Enter an Account Number or GL Account Number to search.');
      return;
    }

    setSearchLoading(true);
    setSearchError('');

    try {
      const result = await loanMappingService.searchByAccount(
        accNo || undefined,
        glAccNo || undefined
      );

      if (!result) {
        setSearchError('No matching loan mapping found. Please verify the account number.');
        setSearchPerformed(true);
        return;
      }

      onFormDataChange({
        ...formData,
        closingMappingId: String(result.id),
        closingMappingRef: `MAP-${String(result.id).substring(0, 8).toUpperCase()}`,
        closingCustomerName: result.customerName ?? '',
        closingCustomerId: result.customerId ?? '',
        closingLoanAccountNumber: result.addonLoanAccountNumber ?? '',
        approvedAmount: result.approvedAmount,
        addonOutstandingPrincipal: '',
        addonOutstandingInterest: '',
        closingGLs: (result.mappedGls ?? [])
          .filter((gl) => gl.statusCode === 'OPEN')
          .map((gl) => ({
            mappedGlId: String(gl.id), glNumber: gl.glNumber ?? '', customerName: gl.glCustomerName ?? '',
            netWeight: gl.netWeight, allocationRatio: gl.allocationRatio,
            selected: false, outstandingPrincipal: '', outstandingInterest: '',
          })),
      });
      setSearchPerformed(true);
      setSearchError('');
    } catch {
      setSearchError('Failed to search. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleResetSearch = () => {
    setSearchAccountNo('');
    setSearchGLAccountNo('');
    setSearchPerformed(false);
    setSearchError('');
    onFormDataChange({
      ...formData,
      closingMappingId: '', closingMappingRef: '', closingCustomerName: '', closingCustomerId: '',
      closingLoanAccountNumber: '', approvedAmount: 0, addonOutstandingPrincipal: '', addonOutstandingInterest: '', closingGLs: [],
    });
  };

  // ---- Closing GL handlers ----
  const handleClosingGLToggle = (mappedGlId: string) => {
    onFormDataChange({
      ...formData,
      closingGLs: formData.closingGLs.map((gl) =>
        gl.mappedGlId === mappedGlId ? { ...gl, selected: !gl.selected } : gl
      ),
    });
  };

  const handleClosingGLField = (mappedGlId: string, field: 'outstandingPrincipal' | 'outstandingInterest', value: string) => {
    onFormDataChange({
      ...formData,
      closingGLs: formData.closingGLs.map((gl) =>
        gl.mappedGlId === mappedGlId ? { ...gl, [field]: value } : gl
      ),
    });
  };

  // ---- New lot handlers ----
  const handleNewLotCustomerChange = (customerId: string) => {
    const name = customerNameLookup[customerId] || '';
    onFormDataChange({
      ...formData,
      newLotCustomerId: customerId,
      newLotCustomerName: name,
      newLotGLs: formData.newLotGLs.map((gl) => ({ ...gl, glCustomerId: customerId, glCustomerName: name })),
    });
  };

  const handleOpenCustomerModal = (source: 'section' | string = 'section') => {
    setCustomerFormData({ customerId: '', customerName: '' });
    setCustomerModalSource(source);
    setCustomerModalOpen(true);
  };

  const handleSaveNewCustomer = async () => {
    const { customerId, customerName } = customerFormData;
    if (!customerId.trim() || !customerName.trim()) return;
    const cleanId = customerId.toUpperCase().replace(/\s/g, '');
    const trimmedName = customerName.trim();
    try {
      const created = await createCustomer({
        customerId: cleanId,
        customerName: trimmedName,
      });
      const uuid = String(created.id);
      await refetchCustomers();
      if (customerModalSource === 'section') {
        onFormDataChange({
          ...formData,
          newLotCustomerId: uuid,
          newLotCustomerName: trimmedName,
          newLotGLs: formData.newLotGLs.map((gl) => ({ ...gl, glCustomerId: uuid, glCustomerName: trimmedName })),
        });
      } else {
        onFormDataChange({
          ...formData,
          newLotGLs: formData.newLotGLs.map((gl) =>
            gl.id === customerModalSource ? { ...gl, glCustomerId: uuid, glCustomerName: trimmedName } : gl
          ),
        });
      }
      setCustomerModalOpen(false);
    } catch (err: any) {
      const detail = err?.response?.data;
      alert(detail?.error?.message || detail?.message || (typeof detail === 'string' ? detail : null) || err?.message || 'Failed to create customer');
    }
  };

  const handleGLCustomerChange = (glId: string, customerId: string) => {
    const name = customerNameLookup[customerId] || '';
    onFormDataChange({
      ...formData,
      newLotGLs: formData.newLotGLs.map((gl) =>
        gl.id === glId ? { ...gl, glCustomerId: customerId, glCustomerName: name } : gl
      ),
    });
  };

  const handleNewLotGLField = (id: string, field: keyof NewLotGLFormData, value: string) => {
    onFormDataChange({
      ...formData,
      newLotGLs: formData.newLotGLs.map((gl) => {
        if (gl.id !== id) return gl;
        const updated = { ...gl, [field]: value };
        // CR-002 Loan Base convention: outstanding defaults from the original amount until edited independently
        if (field === 'originalLoanAmount' && (!gl.outstandingAmount || gl.outstandingAmount === gl.originalLoanAmount)) {
          updated.outstandingAmount = value;
        }
        return updated;
      }),
    });
  };

  const addNewLotGL = () => {
    const firstGL = formData.newLotGLs[0];
    onFormDataChange({
      ...formData,
      newLotGLs: [...formData.newLotGLs, {
        id: `NGL_${Date.now()}`,
        glCustomerId: firstGL?.glCustomerId || '',
        glCustomerName: firstGL?.glCustomerName || '',
        glNumber: '', netWeight: '', originalLoanAmount: '', outstandingAmount: '',
        loanDate: new Date().toISOString().split('T')[0],
      }],
    });
  };

  const removeNewLotGL = (id: string) => {
    if (formData.newLotGLs.length <= 1) return;
    onFormDataChange({ ...formData, newLotGLs: formData.newLotGLs.filter((gl) => gl.id !== id) });
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2 className="modal-title">
            {mode === 'create' ? 'New Combined Transaction' : 'Edit Combined Transaction'}
          </h2>
          <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          {/* ========== CLOSING LEG ========== */}
          <div style={{ background: 'rgba(239, 68, 68, 0.04)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#dc2626', marginBottom: '1rem' }}>
              CLOSING LEG — Close GL(s) from Existing Mapping
            </h3>

            {/* Search Interface (create mode, before search) */}
            {mode === 'create' && !isSearchDone && (
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                  Search by Account Number or GL Account Number to find the loan mapping.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '1rem', alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Account Number</label>
                    <input className="form-input" type="text" value={searchAccountNo}
                      onChange={(e) => setSearchAccountNo(e.target.value.replace(/\s/g, ''))}
                      placeholder="e.g., ACC-2025-001" style={{ border: '1px solid #334155' }}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">GL Account Number</label>
                    <input className="form-input" type="text" value={searchGLAccountNo}
                      onChange={(e) => setSearchGLAccountNo(e.target.value.replace(/\s/g, ''))}
                      placeholder="e.g., GL-2000" style={{ border: '1px solid #334155' }}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
                  </div>
                  <button className="btn btn-primary" onClick={handleSearch} disabled={searchLoading}
                    style={{ height: '34px', display: 'flex', alignItems: 'center', gap: '0.375rem', whiteSpace: 'nowrap', ...(searchLoading ? { opacity: 0.7, cursor: 'wait' } : {}) }}>
                    <IconSystem.search size={16} /> {searchLoading ? 'Searching...' : 'Search'}
                  </button>
                </div>
                {searchError && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem', padding: '0.625rem 0.875rem',
                    background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.375rem', color: '#dc2626', fontSize: '0.8rem' }}>
                    <IconSystem.alert size={14} /> {searchError}
                  </div>
                )}
              </div>
            )}

            {/* Found Loan Details (read-only) */}
            {isSearchDone && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem',
                  background: 'var(--bg-secondary)', padding: '0.75rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Mapping Reference</div>
                    <div style={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.85rem' }}>{formData.closingMappingRef}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Customer</div>
                    <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{formData.closingCustomerName} ({formData.closingCustomerId})</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Account Number</div>
                    <div style={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.85rem' }}>{formData.closingLoanAccountNumber}</div>
                  </div>
                </div>
                {mode === 'create' && (
                  <button type="button" onClick={handleResetSearch}
                    style={{ marginTop: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: '0.75rem', textDecoration: 'underline' }}>
                    ← Search Again
                  </button>
                )}
              </div>
            )}

            {/* Add-On Loan Outstanding */}
            {isSearchDone && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Outstanding Principal *</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.addonOutstandingPrincipal}
                    onChange={(e) => onFormDataChange({ ...formData, addonOutstandingPrincipal: e.target.value })}
                    placeholder="0"
                    min={0}
                    step="1"
                    style={{ border: `1px solid ${validated && !formData.addonOutstandingPrincipal ? '#dc2626' : '#334155'}`, fontSize: '0.85rem' }}
                  />
                  {validated && !formData.addonOutstandingPrincipal && (
                    <span style={{ fontSize: '0.7rem', color: '#dc2626', marginTop: '0.2rem', display: 'block' }}>Required</span>
                  )}
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Outstanding Interest *</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.addonOutstandingInterest}
                    onChange={(e) => onFormDataChange({ ...formData, addonOutstandingInterest: e.target.value })}
                    placeholder="0"
                    min={0}
                    step="1"
                    style={{ border: `1px solid ${validated && !formData.addonOutstandingInterest ? '#dc2626' : '#334155'}`, fontSize: '0.85rem' }}
                  />
                  {validated && !formData.addonOutstandingInterest && (
                    <span style={{ fontSize: '0.7rem', color: '#dc2626', marginTop: '0.2rem', display: 'block' }}>Required</span>
                  )}
                </div>
              </div>
            )}

            {/* GL Selection */}
            {isSearchDone && formData.closingGLs.length > 0 && (
              <>
                {validated && closingGLsWithMissing.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.625rem 0.875rem', marginBottom: '0.75rem',
                    background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '0.375rem', color: '#92400e', fontSize: '0.78rem', fontWeight: 500 }}>
                    <IconSystem.warning size={14} style={{ flexShrink: 0, marginTop: '1px' }} />
                    <span>Outstanding amounts required for: <strong>{closingGLsWithMissing.map((gl) => gl.glNumber).join(', ')}</strong></span>
                  </div>
                )}
                <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-tertiary)' }}>
                        <th style={{ padding: '0.4rem', textAlign: 'center', fontWeight: 600, fontSize: '0.7rem', borderBottom: '2px solid var(--border-color)', width: '35px' }}>Sel</th>
                        <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: 600, fontSize: '0.7rem', borderBottom: '2px solid var(--border-color)' }}>GL #</th>
                        <th style={{ padding: '0.4rem', textAlign: 'right', fontWeight: 600, fontSize: '0.7rem', borderBottom: '2px solid var(--border-color)' }}>Weight</th>
                        <th style={{ padding: '0.4rem', textAlign: 'right', fontWeight: 600, fontSize: '0.7rem', borderBottom: '2px solid var(--border-color)' }}>Ratio</th>
                        <th style={{ padding: '0.4rem', textAlign: 'right', fontWeight: 600, fontSize: '0.7rem', borderBottom: '2px solid var(--border-color)' }}>O/S Principal</th>
                        <th style={{ padding: '0.4rem', textAlign: 'right', fontWeight: 600, fontSize: '0.7rem', borderBottom: '2px solid var(--border-color)' }}>O/S Interest</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.closingGLs.map((gl) => {
                        const isMissing = validated && gl.selected && (!gl.outstandingPrincipal || !gl.outstandingInterest);
                        return (
                          <tr key={gl.mappedGlId} style={{ borderBottom: '1px solid var(--border-color)',
                            background: isMissing ? 'rgba(245, 158, 11, 0.06)' : gl.selected ? 'rgba(239, 68, 68, 0.05)' : 'transparent' }}>
                            <td style={{ padding: '0.4rem', textAlign: 'center' }}>
                              <input type="checkbox" checked={gl.selected} onChange={() => handleClosingGLToggle(gl.mappedGlId)} style={{ cursor: 'pointer' }} />
                            </td>
                            <td style={{ padding: '0.4rem', fontFamily: 'monospace', fontSize: '0.75rem' }}>{gl.glNumber}</td>
                            <td style={{ padding: '0.4rem', textAlign: 'right' }}>{gl.netWeight.toFixed(3)}</td>
                            <td style={{ padding: '0.4rem', textAlign: 'right' }}>{(gl.allocationRatio * 100).toFixed(2)}%</td>
                            <td style={{ padding: '0.4rem', textAlign: 'right' }}>
                              <input className="form-input" type="number" min="0" step="1" placeholder="0" value={gl.outstandingPrincipal}
                                onChange={(e) => handleClosingGLField(gl.mappedGlId, 'outstandingPrincipal', e.target.value)}
                                disabled={!gl.selected} style={{ width: '110px', textAlign: 'right', fontSize: '0.75rem', padding: '0.2rem 0.4rem',
                                  borderColor: isMissing && !gl.outstandingPrincipal ? '#f59e0b' : undefined }} />
                            </td>
                            <td style={{ padding: '0.4rem', textAlign: 'right' }}>
                              <input className="form-input" type="number" min="0" step="1" placeholder="0" value={gl.outstandingInterest}
                                onChange={(e) => handleClosingGLField(gl.mappedGlId, 'outstandingInterest', e.target.value)}
                                disabled={!gl.selected} style={{ width: '110px', textAlign: 'right', fontSize: '0.75rem', padding: '0.2rem 0.4rem',
                                  borderColor: isMissing && !gl.outstandingInterest ? '#f59e0b' : undefined }} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* Closing Settlement Summary */}
            {isSearchDone && selectedClosingGLs.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem',
                background: 'rgba(239, 68, 68, 0.06)', padding: '0.75rem', borderRadius: '0.375rem' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Combined Ratio</div>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{(closingCombinedRatio * 100).toFixed(2)}%</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>GL Settlement (P+I)</div>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{formatCurrency(totalClosingGLPI)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Add-On Settlement (P+I)</div>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{formatCurrency(Math.round(settlementP + settlementI))}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Total Settlement</div>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#dc2626' }}>{formatCurrency(closingTotalSettlement)}</div>
                </div>
              </div>
            )}
          </div>

          {/* ========== NEW LOT LEG ========== */}
          <div style={{ background: 'rgba(34, 197, 94, 0.04)', border: '1px solid rgba(34, 197, 94, 0.2)', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#16a34a', marginBottom: '1rem' }}>
              NEW LOT LEG — Create New Add-On Request
            </h3>

            {/* Customer & Loan Type */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem' }}>
                  <div style={{ flex: 1 }}>
                    <SyutSelect
                      label="Customer *"
                      options={customerOptions}
                      value={formData.newLotCustomerId}
                      onChange={(val) => handleNewLotCustomerChange((val as string) || '')}
                      placeholder="Select Customer..."
                      isSearchable
                      isClearable={false}
                      size="sm"
                    />
                  </div>
                  <button type="button" onClick={() => handleOpenCustomerModal('section')} title="Add New Customer"
                    style={{ background: 'transparent', color: 'var(--text-secondary)', border: '1px solid #334155', borderRadius: '0.375rem',
                      width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s ease' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-tertiary)'; e.currentTarget.style.color = 'var(--primary)'; e.currentTarget.style.borderColor = 'var(--primary)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = '#334155'; }}>
                    <IconSystem.add size={16} />
                  </button>
                </div>
                {validated && !formData.newLotCustomerId && (
                  <span style={{ fontSize: '0.7rem', color: '#dc2626', marginTop: '0.2rem', display: 'block' }}>Required</span>
                )}
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <SyutSelect
                  label="Loan Type *"
                  options={loanTypeOptions}
                  value={formData.newLotLoanType}
                  onChange={(val) => onFormDataChange({ ...formData, newLotLoanType: (val as string) || '' })}
                  placeholder="Select Loan Type..."
                  isSearchable isClearable={false} size="sm"
                />
                {validated && !formData.newLotLoanType && (
                  <span style={{ fontSize: '0.7rem', color: '#dc2626', marginTop: '0.2rem', display: 'block' }}>Required</span>
                )}
              </div>
            </div>

            {/* Account Number */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Account Number * <span style={{ fontWeight: 400, fontSize: '0.7rem', color: 'var(--text-muted)' }}>(14 characters)</span></label>
                <input className="form-input" placeholder="14-character account number" value={formData.newLotAccountNumber}
                  maxLength={14}
                  onChange={(e) => onFormDataChange({ ...formData, newLotAccountNumber: e.target.value.replace(/\s/g, '') })}
                  style={{ border: `1px solid ${newLotAccNumError || (validated && !formData.newLotAccountNumber.trim()) ? '#dc2626' : '#334155'}` }} />
                {validated && !formData.newLotAccountNumber.trim() && (
                  <span style={{ fontSize: '0.7rem', color: '#dc2626', marginTop: '0.2rem', display: 'block' }}>Required</span>
                )}
                {!validated && newLotAccNumError && (
                  <span style={{ fontSize: '0.7rem', color: 'var(--danger, #dc2626)', marginTop: '0.2rem', display: 'block' }}>{newLotAccNumError}</span>
                )}
                {validated && formData.newLotAccountNumber.trim() && newLotAccNumError && (
                  <span style={{ fontSize: '0.7rem', color: 'var(--danger, #dc2626)', marginTop: '0.2rem', display: 'block' }}>{newLotAccNumError}</span>
                )}
              </div>
            </div>

            {/* New Lot GLs */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label className="form-label" style={{ marginBottom: 0 }}>Gold Loans for New Lot</label>
              <button className="btn btn-secondary" style={{ fontSize: '0.7rem', padding: '0.2rem 0.6rem' }} onClick={addNewLotGL}>
                <IconSystem.add size={14} /> Add GL
              </button>
            </div>
            {validated && newLotGLsWithMissing.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.625rem 0.875rem', marginBottom: '0.75rem',
                background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '0.375rem', color: '#92400e', fontSize: '0.78rem', fontWeight: 500 }}>
                <IconSystem.warning size={14} style={{ flexShrink: 0, marginTop: '1px' }} />
                <span>Complete all required fields for GL entries</span>
              </div>
            )}
            <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-tertiary)' }}>
                    <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: 600, fontSize: '0.7rem', borderBottom: '2px solid var(--border-color)', minWidth: '300px' }}>GL Customer ID</th>
                    <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: 600, fontSize: '0.7rem', borderBottom: '2px solid var(--border-color)', minWidth: '120px' }}>GL Number</th>
                    <th style={{ padding: '0.4rem', textAlign: 'right', fontWeight: 600, fontSize: '0.7rem', borderBottom: '2px solid var(--border-color)', minWidth: '100px' }}>Eligible Weight (g)</th>
                    <th style={{ padding: '0.4rem', textAlign: 'right', fontWeight: 600, fontSize: '0.7rem', borderBottom: '2px solid var(--border-color)', minWidth: '130px' }}>Original Amount</th>
                    {isGrossBasis && (
                      <th style={{ padding: '0.4rem', textAlign: 'right', fontWeight: 600, fontSize: '0.7rem', borderBottom: '2px solid var(--border-color)', minWidth: '130px' }}>Outstanding *</th>
                    )}
                    <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: 600, fontSize: '0.7rem', borderBottom: '2px solid var(--border-color)', minWidth: '120px' }}>Loan Date</th>
                    <th style={{ padding: '0.4rem', textAlign: 'center', fontWeight: 600, fontSize: '0.7rem', borderBottom: '2px solid var(--border-color)', width: '35px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.newLotGLs.map((gl) => {
                    const glHasMissing = validated && (!gl.glCustomerId || !gl.glNumber.trim() || !gl.netWeight || !gl.originalLoanAmount || !gl.loanDate);
                    return (
                    <tr key={gl.id} style={{ borderBottom: '1px solid var(--border-color)',
                      background: glHasMissing ? 'rgba(245, 158, 11, 0.06)' : 'transparent' }}>
                      <td style={{ padding: '0.4rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                          <div style={{ flex: 1 }}>
                            <SyutSelect
                              options={customerOptions}
                              value={gl.glCustomerId}
                              onChange={(val) => handleGLCustomerChange(gl.id, (val as string) || '')}
                              placeholder="Select..."
                              isSearchable
                              isClearable={false}
                              size="sm"
                              menuPlacement="auto"
                              menuPortalTarget={document.body}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => handleOpenCustomerModal(gl.id)}
                            title="Add New Customer"
                            style={{
                              background: 'transparent', color: 'var(--text-secondary)', border: '1px solid #334155', borderRadius: '0.25rem',
                              width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s ease',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-tertiary)'; e.currentTarget.style.color = 'var(--primary)'; e.currentTarget.style.borderColor = 'var(--primary)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = '#334155'; }}
                          >
                            <IconSystem.add size={12} />
                          </button>
                        </div>
                      </td>
                      <td style={{ padding: '0.4rem' }}>
                        <input className="form-input" placeholder="14-char GL Number" value={gl.glNumber}
                          maxLength={14}
                          onChange={(e) => handleNewLotGLField(gl.id, 'glNumber', e.target.value)}
                          style={{ fontSize: '0.75rem', padding: '0.2rem 0.4rem', fontFamily: 'monospace',
                            borderColor: newLotGLNumberErrors[formData.newLotGLs.indexOf(gl)] ? '#dc2626'
                              : (validated && !gl.glNumber.trim() ? '#f59e0b' : undefined) }} />
                        {newLotGLNumberErrors[formData.newLotGLs.indexOf(gl)] && (
                          <div style={{ fontSize: '0.68rem', color: 'var(--danger, #dc2626)', marginTop: '0.1rem', whiteSpace: 'nowrap' }}>
                            {newLotGLNumberErrors[formData.newLotGLs.indexOf(gl)]}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.4rem' }}>
                        <input className="form-input" type="number" min="0" step="0.001" placeholder="0.000" value={gl.netWeight}
                          onChange={(e) => handleNewLotGLField(gl.id, 'netWeight', e.target.value)}
                          style={{ width: '100px', textAlign: 'right', fontSize: '0.75rem', padding: '0.2rem 0.4rem',
                            borderColor: validated && !gl.netWeight ? '#f59e0b' : undefined }} />
                      </td>
                      <td style={{ padding: '0.4rem' }}>
                        <input className="form-input" type="number" min="0" step="1000" placeholder="0" value={gl.originalLoanAmount}
                          onChange={(e) => handleNewLotGLField(gl.id, 'originalLoanAmount', e.target.value)}
                          style={{ width: '120px', textAlign: 'right', fontSize: '0.75rem', padding: '0.2rem 0.4rem',
                            borderColor: validated && !gl.originalLoanAmount ? '#f59e0b' : undefined }} />
                      </td>
                      {isGrossBasis && (
                        <td style={{ padding: '0.4rem' }}>
                          <input className="form-input" type="number" min="0" step="1000" placeholder="0" value={gl.outstandingAmount}
                            onChange={(e) => handleNewLotGLField(gl.id, 'outstandingAmount', e.target.value)}
                            title="Outstanding GL principal — deducted from gross gold value"
                            style={{ width: '120px', textAlign: 'right', fontSize: '0.75rem', padding: '0.2rem 0.4rem',
                              borderColor: validated && !(parseFloat(gl.outstandingAmount) > 0) ? '#f59e0b' : undefined }} />
                        </td>
                      )}
                      <td style={{ padding: '0.4rem' }}>
                        <input className="form-input" type="date" value={gl.loanDate}
                          onChange={(e) => handleNewLotGLField(gl.id, 'loanDate', e.target.value)}
                          max={new Date().toISOString().split('T')[0]}
                          style={{ fontSize: '0.75rem', padding: '0.2rem 0.4rem',
                            borderColor: validated && !gl.loanDate ? '#f59e0b' : undefined }} />
                      </td>
                      <td style={{ padding: '0.4rem', textAlign: 'center' }}>
                        {formData.newLotGLs.length > 1 && (
                          <button onClick={() => removeNewLotGL(gl.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '0.2rem' }}>
                            <IconSystem.trash size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Eligibility Summary */}
            {newLotTotalWeight > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem',
                background: 'rgba(34, 197, 94, 0.06)', padding: '0.75rem', borderRadius: '0.375rem', marginBottom: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Total Weight</div>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{newLotTotalWeight.toFixed(3)} g</div>
                </div>
                {isGrossBasis && (
                  <>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Gross Gold Value</div>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{formatCurrency(newLotGrossValue)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Less: GL Outstanding</div>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#dc2626' }}>−{formatCurrency(newLotTotalOutstanding)}</div>
                    </div>
                  </>
                )}
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Eligible Amount</div>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: newLotEligible > 0 ? '#16a34a' : '#dc2626' }}>{formatCurrency(newLotEligible)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Max Permissible</div>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--primary)' }}>{formatCurrency(newLotMaxPermissible)}</div>
                </div>
              </div>
            )}

            {/* Requested Amount (after eligibility) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Requested Amount *</label>
                <input className="form-input" type="number" min="0" step="1000" placeholder="Enter amount"
                  value={formData.newLotRequestedAmount}
                  onChange={(e) => onFormDataChange({ ...formData, newLotRequestedAmount: e.target.value })}
                  style={{ border: `2px solid ${newLotIndicator.color}`, fontWeight: 600, fontSize: '1rem' }} />
                {newLotIndicator.label && (
                  <span style={{ fontSize: '0.7rem', color: newLotIndicator.color, fontWeight: 600, marginTop: '0.25rem', display: 'block' }}>
                    {newLotIndicator.label}
                  </span>
                )}
                {validated && (!formData.newLotRequestedAmount || parseFloat(formData.newLotRequestedAmount) <= 0) && (
                  <span style={{ fontSize: '0.7rem', color: '#dc2626', marginTop: '0.2rem', display: 'block' }}>Required</span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', paddingTop: '1.25rem' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  Max: <strong style={{ color: 'var(--primary)' }}>{formatCurrency(newLotMaxPermissible)}</strong>
                </span>
              </div>
            </div>
          </div>

          {/* ========== NET CASH FLOW ========== */}
          {(selectedClosingGLs.length > 0 || newLotRequested > 0) && (
            <div style={{
              background: netCashFlow >= 0 ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.08)',
              border: `2px solid ${netCashFlow >= 0 ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
              borderRadius: '0.5rem', padding: '1rem', textAlign: 'center',
            }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>NET CASH FLOW</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: netCashFlow >= 0 ? '#16a34a' : '#dc2626' }}>
                {netCashFlow >= 0 ? '+' : ''}{formatCurrency(netCashFlow)}
              </div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                {netCashFlow >= 0 ? 'Customer receives funds' : 'Customer must bring in shortfall'}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                (New Lot {formatCurrency(newLotRequested)} + GL Original {formatCurrency(newLotTotalGLAmount)}) − Closing Settlement ({formatCurrency(closingTotalSettlement)})
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <div>
            {exceedsMax && (
              <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>
                Requested amount exceeds maximum permissible ({formatCurrency(newLotMaxPermissible)})
              </span>
            )}
            {!exceedsMax && eligibilityBlocked && (
              <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>
                New lot eligible amount is {formatCurrency(newLotEligible)} — GL outstanding equals or exceeds the gross gold value
              </span>
            )}
            {!exceedsMax && hasAccountErrors && (
              <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>
                Account / GL numbers must be exactly 14 characters
              </span>
            )}
            {validated && isSearchDone && (!formData.addonOutstandingPrincipal || !formData.addonOutstandingInterest) && (
              <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>
                Outstanding Principal and Interest are mandatory
              </span>
            )}
            {validated && closingGLsWithMissing.length > 0 && (
              <span style={{ fontSize: '0.75rem', color: '#d97706', fontWeight: 600 }}>
                Missing GL outstanding amounts
              </span>
            )}
            {validated && (!formData.newLotCustomerId || !formData.newLotLoanType || !formData.newLotAccountNumber.trim()) && (
              <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>
                Complete all required New Lot fields
              </span>
            )}
            {validated && newLotGLsWithMissing.length > 0 && (
              <span style={{ fontSize: '0.75rem', color: '#d97706', fontWeight: 600 }}>
                Incomplete GL entries in New Lot
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={() => { setValidated(true); onSave(); }} disabled={exceedsMax || hasAccountErrors || eligibilityBlocked}
              style={exceedsMax || hasAccountErrors || eligibilityBlocked ? { opacity: 0.5, cursor: 'not-allowed' } : {}}>
              <IconSystem.save size={16} />
              Save as Draft
            </button>
            {onSubmit && (
              <button className="btn btn-primary" onClick={() => { setValidated(true); onSubmit(); }} disabled={exceedsMax || hasAccountErrors || eligibilityBlocked}
                style={{ background: '#16a34a', borderColor: '#16a34a', ...(exceedsMax || hasAccountErrors || eligibilityBlocked ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}>
                <IconSystem.check size={16} /> Submit
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Customer Registry Modal */}
      <CustomerFormModal
        isOpen={customerModalOpen}
        mode="create"
        formData={customerFormData}
        onFormDataChange={setCustomerFormData}
        onSave={handleSaveNewCustomer}
        onClose={() => setCustomerModalOpen(false)}
      />
    </>
  );
};

// ============================================================================
// View Modal — fetches detail from API
// ============================================================================

interface CombinedViewModalProps {
  isOpen: boolean;
  recordId: string | null;
  onClose: () => void;
}

export const CombinedViewModal: React.FC<CombinedViewModalProps> = ({
  isOpen,
  recordId,
  onClose,
}) => {
  const { data: loanTypeEnums } = useEnumsByGroup('LOAN_TYPE');
  const loanTypeLookup = useMemo(() => {
    const map: Record<string, string> = {};
    loanTypeEnums.forEach(e => { map[e.enumKey] = e.enumValue; });
    return map;
  }, [loanTypeEnums]);

  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approvalInfo, setApprovalInfo] = useState<{
    approvedBy?: string; approvedAt?: string;
    rejectedBy?: string; rejectedAt?: string; rejectedRemark?: string | null;
  }>({});

  useEffect(() => {
    if (!isOpen || !recordId) { setDetail(null); setApprovalInfo({}); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setApprovalInfo({});
    combinedService.getById(recordId).then(res => {
      if (!cancelled) setDetail(res.data);
    }).catch(() => {
      if (!cancelled) setError('Failed to load combined transaction details');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    approvalService.getHistory('combined', recordId).then(res => {
      if (cancelled) return;
      const history = res.data ?? [];
      const approved = history.find(h => h.action?.toLowerCase() === 'approved');
      const rejected = history.find(h => h.action?.toLowerCase() === 'rejected');
      setApprovalInfo({
        approvedBy: approved?.actionedByName, approvedAt: approved?.actionedAt,
        rejectedBy: rejected?.actionedByName, rejectedAt: rejected?.actionedAt, rejectedRemark: rejected?.remark,
      });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen, recordId]);

  if (!isOpen || !recordId) return null;

  const closing = detail?.closingDetail;
  const addon = detail?.newRequestDetail;
  const statusCode = detail?.statusCode ?? '';
  const netCashFlow = detail?.netCashFlow ?? 0;
  const closingGLs: any[] = closing?.glEntries ?? [];
  const addonGLs: any[] = addon?.glEntries ?? [];
  const combinedRef = `CMB-${String(recordId).substring(0, 8).toUpperCase()}`;
  const closingRef = detail?.closingTransactionId ? `CLO-${String(detail.closingTransactionId).substring(0, 8).toUpperCase()}` : '-';
  const newRequestRef = detail?.newRequestId ? `REQ-${String(detail.newRequestId).substring(0, 8).toUpperCase()}` : '-';
  const mappingRef = closing?.mappingId ? `MAP-${String(closing.mappingId).substring(0, 8).toUpperCase()}` : (closing?.mappingRef ?? '-');

  const totalClosingGLPI = closingGLs.reduce((s: number, gl: any) => s + (gl.outstandingPrincipal ?? 0) + (gl.outstandingInterest ?? 0), 0);

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            Combined {combinedRef}
            <span style={getStatusBadgeStyle(statusCode)}>{(statusCode ?? '').replace(/_/g, ' ')}</span>
          </h2>
          <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          {loading && (
            <ModalLoadingSkeleton sections={3} showTable tableCols={5} tableRows={3} />
          )}
          {error && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem', color: 'var(--danger)' }}>
              <div>{error}</div>
            </div>
          )}
          {!loading && !error && detail && (
            <>
          {/* Section 1: Closing Leg Card */}
          <div style={{ marginBottom: '1.5rem', background: 'rgba(239, 68, 68, 0.04)', padding: '1rem', borderRadius: '0.5rem', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#dc2626', margin: '0 0 0.75rem 0', borderBottom: '2px solid #dc2626', paddingBottom: '0.5rem' }}>
              Closing Leg — {closingRef}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Mapping Reference</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem', fontFamily: 'monospace' }}>{mappingRef}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Account Number</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem', fontFamily: 'monospace' }}>{closing?.loanAccountNumber ?? '-'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Customer ID</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem', fontFamily: 'monospace' }}>{closing?.customerId ?? '-'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Customer Name</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{closing?.customerName ?? '-'}</div>
              </div>
            </div>

            {/* Closing GL Table */}
            <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-tertiary)' }}>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>#</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>GL Number</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Eligible Weight (g)</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Alloc. Ratio</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Outstanding P</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Outstanding I</th>
                  </tr>
                </thead>
                <tbody>
                  {closingGLs.map((gl: any, idx: number) => (
                    <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '0.5rem', color: 'var(--text-muted)' }}>{idx + 1}</td>
                      <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>{gl.glNumber ?? gl.glAccountNumber ?? '-'}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{(gl.netWeight ?? 0).toFixed(3)}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 500 }}>{((gl.allocationRatio ?? 0) * 100).toFixed(2)}%</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(gl.outstandingPrincipal ?? 0)}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(gl.outstandingInterest ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Settlement Summary */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem',
              background: 'rgba(239, 68, 68, 0.06)', padding: '0.75rem', borderRadius: '0.375rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Combined Ratio</div>
                <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{((closing?.combinedRatio ?? 0) * 100).toFixed(2)}%</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>GL Settlement (P+I)</div>
                <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{formatCurrency(totalClosingGLPI)}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Add-On Settlement (P+I)</div>
                <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{formatCurrency((closing?.addonSettlementPrincipal ?? 0) + (closing?.addonSettlementInterest ?? 0))}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Total Settlement</div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#dc2626' }}>{formatCurrency(totalClosingGLPI + (closing?.addonSettlementPrincipal ?? 0) + (closing?.addonSettlementInterest ?? 0))}</div>
              </div>
            </div>
          </div>

          {/* Section 2: New Lot Leg Card */}
          <div style={{ marginBottom: '1.5rem', background: 'rgba(34, 197, 94, 0.04)', padding: '1rem', borderRadius: '0.5rem', border: '1px solid rgba(34, 197, 94, 0.2)' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#16a34a', margin: '0 0 0.75rem 0', borderBottom: '2px solid #16a34a', paddingBottom: '0.5rem' }}>
              New Lot Leg — {newRequestRef}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Customer Name</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{addon?.customerName ?? '-'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Loan Type</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{loanTypeLookup[addon?.addonLoanType] || addon?.addonLoanType || '-'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Account Number</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem', fontFamily: 'monospace' }}>{addon?.addonLoanAccountNumber ?? '-'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Requested Amount</div>
                <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#16a34a' }}>{formatCurrency(addon?.requestedAmount ?? 0)}</div>
              </div>
            </div>

            {/* New Lot GL Table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-tertiary)' }}>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>#</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>GL Number</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Customer</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Eligible Weight (g)</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Original Amount</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Loan Date</th>
                  </tr>
                </thead>
                <tbody>
                  {addonGLs.map((gl: any, idx: number) => (
                    <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '0.5rem', color: 'var(--text-muted)' }}>{idx + 1}</td>
                      <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>{gl.glNumber ?? '-'}</td>
                      <td style={{ padding: '0.5rem' }}>{gl.customerName || '-'}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{(gl.netWeight ?? 0).toFixed(3)}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(gl.originalLoanAmount ?? 0)}</td>
                      <td style={{ padding: '0.5rem' }}>{formatDate(gl.loanDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 3: Net Cash Flow */}
          <div style={{
            background: netCashFlow >= 0 ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.08)',
            border: `2px solid ${netCashFlow >= 0 ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
            borderRadius: '0.5rem', padding: '1rem', textAlign: 'center', marginBottom: '1.5rem',
          }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>NET CASH FLOW</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: netCashFlow >= 0 ? '#16a34a' : '#dc2626' }}>
              {netCashFlow >= 0 ? '+' : ''}{formatCurrency(netCashFlow)}
            </div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              {netCashFlow >= 0 ? 'Customer receives funds' : 'Customer must bring in shortfall'}
            </div>
          </div>

          {/* Section 4: Submission & Approval Info Card */}
          <div style={{ marginBottom: '1.5rem', background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.75rem 0', borderBottom: '2px solid var(--primary)', paddingBottom: '0.5rem' }}>
              Submission & Approval Info
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Created By</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{closing?.createdByName || '-'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Created At</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{formatDate(detail?.createdAt)}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Submitted By</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{detail?.submittedByName || detail?.submittedBy || '-'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Submitted At</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{detail?.submittedAt ? formatDate(detail.submittedAt) : '-'}</div>
              </div>
            </div>
            {approvalInfo.approvedBy && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', marginTop: '1rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Approved By</div>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#16a34a' }}>{approvalInfo.approvedBy}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Approved At</div>
                  <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{approvalInfo.approvedAt ? formatDate(approvalInfo.approvedAt) : '-'}</div>
                </div>
              </div>
            )}
            {approvalInfo.rejectedBy && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', marginTop: '1rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Rejected By</div>
                    <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#dc2626' }}>{approvalInfo.rejectedBy}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Rejected At</div>
                    <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{approvalInfo.rejectedAt ? formatDate(approvalInfo.rejectedAt) : '-'}</div>
                  </div>
                </div>
                {approvalInfo.rejectedRemark && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Rejection Remark</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{approvalInfo.rejectedRemark}</div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Section 5: Workflow Information — Returned History
              Shown for RETURNED status (active return cycle) AND for REJECTED records
              that came from a prior return, so the return trail stays visible after rejection. */}
          {(statusCode === 'RETURNED' || approvalInfo.rejectedBy) && (
            <ReturnedHistorySection recordType="combined" recordId={recordId} />
          )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};
